import type { ReactNode } from 'react';
import { Badge, Button, Card, CardContent, CardDescription, CardTitle } from '@auctisync/ui';
import { Layout } from './Layout';
import { PageHead } from './PageHead';

/*
 * Reusable building blocks for marketing sub-pages.
 *
 * Each landing page under auctisync.com has the same rough anatomy:
 *   Eyebrow badge + headline + subhead (Hero)
 *   Pillar grid ("what you get")
 *   Optional feature-deep section
 *   Proof / anchor customer
 *   CTA strip
 *
 * These components render that anatomy so individual pages stay in the
 * "data config" layer — the copy drives the page, not the markup.
 */

interface MarketingPageProps {
  seoTitle: string;
  seoDescription: string;
  canonicalPath: string;
  eyebrow: string;
  headline: string;
  subhead: string;
  children: ReactNode;
}

export function MarketingPage({
  seoTitle,
  seoDescription,
  canonicalPath,
  eyebrow,
  headline,
  subhead,
  children,
}: MarketingPageProps) {
  return (
    <Layout>
      <PageHead title={seoTitle} description={seoDescription} canonicalPath={canonicalPath} />
      <Hero eyebrow={eyebrow} headline={headline} subhead={subhead} />
      {children}
    </Layout>
  );
}

function Hero({
  eyebrow,
  headline,
  subhead,
}: {
  eyebrow: string;
  headline: string;
  subhead: string;
}) {
  return (
    <section className="mx-auto max-w-6xl px-6 pt-16 pb-12 md:pt-24 md:pb-16">
      <Badge className="mb-6">{eyebrow}</Badge>
      <h1
        className="max-w-4xl text-4xl leading-tight font-bold tracking-tight md:text-6xl md:leading-[1.05]"
        style={{ color: 'var(--text)' }}
      >
        {headline}
      </h1>
      <p className="mt-6 max-w-2xl text-lg md:text-xl" style={{ color: 'var(--text-muted)' }}>
        {subhead}
      </p>
    </section>
  );
}

/** Grid of short pillars under the hero. */
export function Pillars({
  items,
  title,
}: {
  items: Array<{ title: string; body: string }>;
  title?: string;
}) {
  return (
    <section
      className="border-y py-16"
      style={{ borderColor: 'var(--border)', background: 'var(--surface-muted)' }}
    >
      <div className="mx-auto max-w-6xl px-6">
        {title ? (
          <h2
            className="mb-10 text-3xl font-bold tracking-tight md:text-4xl"
            style={{ color: 'var(--text)' }}
          >
            {title}
          </h2>
        ) : null}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {items.map((item) => (
            <Card key={item.title} className="h-full">
              <CardContent className="flex h-full flex-col gap-2 p-6">
                <CardTitle className="text-lg">{item.title}</CardTitle>
                <CardDescription>{item.body}</CardDescription>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
}

/** Long-form "everything you get" bullet list — readable, not sales-y. */
export function FeatureList({
  title,
  body,
  bullets,
}: {
  title: string;
  body?: string;
  bullets: string[];
}) {
  return (
    <section className="mx-auto max-w-6xl px-6 py-16">
      <div className="grid gap-10 md:grid-cols-[1fr_2fr]">
        <div>
          <h2
            className="text-3xl font-bold tracking-tight md:text-4xl"
            style={{ color: 'var(--text)' }}
          >
            {title}
          </h2>
          {body ? (
            <p className="mt-4 text-lg" style={{ color: 'var(--text-muted)' }}>
              {body}
            </p>
          ) : null}
        </div>
        <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2" style={{ color: 'var(--text)' }}>
          {bullets.map((bullet) => (
            <li key={bullet} className="flex gap-2">
              <span aria-hidden style={{ color: 'var(--brand-accent)' }}>
                →
              </span>
              <span>{bullet}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

/** Side-by-side comparison table for /vs/ pages. */
export function ComparisonTable({
  leftName,
  rightName,
  rows,
}: {
  leftName: string;
  rightName: string;
  rows: Array<{ attribute: string; left: string; right: string }>;
}) {
  return (
    <section className="mx-auto max-w-6xl px-6 py-16">
      <div className="overflow-hidden rounded-lg border" style={{ borderColor: 'var(--border)' }}>
        <table className="w-full text-sm">
          <thead style={{ background: 'var(--surface-muted)' }}>
            <tr>
              <th className="p-4 text-left" style={{ color: 'var(--text-muted)' }}>
                &nbsp;
              </th>
              <th className="p-4 text-left font-bold" style={{ color: 'var(--text)' }}>
                {leftName}
              </th>
              <th className="p-4 text-left" style={{ color: 'var(--text-muted)' }}>
                {rightName}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => (
              <tr
                key={row.attribute}
                style={{ borderTop: idx === 0 ? 'none' : '1px solid var(--border)' }}
              >
                <td
                  className="p-4 font-medium"
                  style={{ color: 'var(--text)', background: 'var(--surface)' }}
                >
                  {row.attribute}
                </td>
                <td
                  className="p-4"
                  style={{ color: 'var(--text)', background: 'var(--surface-elev)' }}
                >
                  {row.left}
                </td>
                <td className="p-4" style={{ color: 'var(--text-muted)' }}>
                  {row.right}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/** CTA strip with an inverted background, at the end of every page. */
export function CtaStrip({
  headline,
  subhead,
  primaryHref = '/contact',
  primaryLabel = 'Request a demo',
  secondaryHref,
  secondaryLabel,
}: {
  headline: string;
  subhead?: string;
  primaryHref?: string;
  primaryLabel?: string;
  secondaryHref?: string;
  secondaryLabel?: string;
}) {
  return (
    <section
      className="border-t py-16"
      style={{ borderColor: 'var(--border)', background: 'var(--surface-inverted)' }}
    >
      <div className="mx-auto max-w-4xl px-6 text-center">
        <h2
          className="text-3xl font-bold tracking-tight md:text-4xl"
          style={{ color: 'var(--text-on-inverted)' }}
        >
          {headline}
        </h2>
        {subhead ? (
          <p className="mx-auto mt-3 max-w-2xl text-lg" style={{ color: 'rgba(255,255,255,0.7)' }}>
            {subhead}
          </p>
        ) : null}
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Button asChild size="lg">
            <a href={primaryHref}>{primaryLabel}</a>
          </Button>
          {secondaryHref && secondaryLabel ? (
            <Button asChild variant="outline" size="lg">
              <a href={secondaryHref}>{secondaryLabel}</a>
            </Button>
          ) : null}
        </div>
      </div>
    </section>
  );
}
